import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import type {
  CreatePaymentTerminalRequest,
  IdResponse,
  PaymentTerminalView,
  UpdatePaymentTerminalRequest,
} from 'shared-models/browser';

export type { PaymentTerminalView } from 'shared-models/browser';

class PaymentTerminalsService {
  // Summary: Loads the cross-tenant terminal snapshot for the super-admin backoffice tab.
  // Details: docs/function-library/web/services.md#payment-terminals-service
  async list(): Promise<PaymentTerminalView[]> {
    try {
      const response = await authenticatedInstance.get<PaymentTerminalView[]>('/payment-terminals');
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Creates a terminal for a tenant; the canonical row returns via the admin SSE echo.
  // Details: docs/function-library/web/services.md#payment-terminals-service
  async create(tenantId: string, payload: CreatePaymentTerminalRequest): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>(
        `/payment-terminals/tenant/${tenantId}`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Updates a terminal (and pinpads); the canonical row returns via the admin SSE echo.
  // Details: docs/function-library/web/services.md#payment-terminals-service
  async update(
    tenantId: string,
    terminalId: number,
    payload: UpdatePaymentTerminalRequest,
  ): Promise<void> {
    try {
      await authenticatedInstance.put<void>(
        `/payment-terminals/tenant/${tenantId}/${terminalId}`,
        payload,
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const paymentTerminalsService = new PaymentTerminalsService();
